import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase/config';
import { doc, setDoc } from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaUser, FaUserTie } from 'react-icons/fa';

export const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      return setError(t('register.passwordsDontMatch'));
    }

    try {
      setError('');
      setLoading(true);
      console.log('Registrando usuario con email:', email);
      const userCredential = await signUp(email, password);
      console.log('Usuario creado en Auth:', userCredential.user.uid);
      // Guardar datos adicionales en Firestore
      if (userCredential && userCredential.user) {
        const userData = {
          firstName,
          lastName,
          email,
          role: 'User',
          createdAt: Timestamp.now(),
          onBoarding: false,
          language: i18n.language
        };
        console.log('Guardando en Firestore:', userData);
        await setDoc(doc(db, 'users', userCredential.user.uid), userData);
        console.log('Documento guardado en Firestore con UID:', userCredential.user.uid);
      }
      // Redirigir al dashboard después de registro
      navigate('/worker-dashboard');
    } catch (error) {
      console.error('Error en el registro:', error);
      setError(t('register.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-full flex justify-center items-center animate-fadeIn">
      <div className="bg-white/95 p-8 rounded-2xl shadow-2xl w-full max-w-4xl relative z-10 backdrop-blur-md border border-white/20 transform transition-all duration-500 hover:scale-[1.02] hover:shadow-3xl">
        {/* Logo con animación */}
        <div className="flex justify-center mb-8">
          <div className="relative group">
            <img 
              src="https://firebasestorage.googleapis.com/v0/b/brix-v2.firebasestorage.app/o/Marca%2FRecurso%205.svg?alt=media&token=65d56a39-541f-4ec0-864f-fc2105cf9c54" 
              alt="Brix Logo" 
              className="w-32 max-h-20 object-contain drop-shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:drop-shadow-2xl"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
          </div>
        </div>

        {/* Título con efecto de gradiente */}
        <h1 className="text-3xl font-bold text-center mb-8 bg-gradient-to-r from-gray-800 via-red-600 to-gray-800 bg-clip-text text-transparent animate-pulse flex items-center justify-center gap-2">
          <FaUser className="text-red-500 animate-pulse" /> {t('register.title')}
        </h1>

        {/* Mensaje de error con animación */}
        {error && (
          <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 rounded-lg animate-shake">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Nombre */}
          <div className="relative group">
            <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
              <FaUser className="text-red-500" /> {t('register.firstName') || 'Nombre'}
            </label>
            <div className="relative">
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoComplete="given-name"
                className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300"
                placeholder="Tu nombre"
              />
              <FaUser className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
            </div>
          </div>

          {/* Apellido */}
          <div className="relative group">
            <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
              <FaUserTie className="text-red-500" /> {t('register.lastName') || 'Apellido'}
            </label>
            <div className="relative">
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                autoComplete="family-name"
                className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300"
                placeholder="Tu apellido"
              />
              <FaUserTie className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
            </div>
          </div>

          {/* Email */}
          <div className="relative group">
            <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
              <FaEnvelope className="text-red-500" /> {t('register.email')}
            </label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300"
                placeholder="tu@email.com"
              />
              <FaEnvelope className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
            </div>
          </div>

          {/* Contraseña */}
          <div className="relative group">
            <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
              <FaLock className="text-red-500" /> {t('register.password')}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full p-4 pl-12 pr-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300"
                placeholder="••••••••"
              />
              <FaLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {/* Confirmar Contraseña */}
          <div className="relative group">
            <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
              <FaLock className="text-red-500" /> {t('register.confirmPassword')}
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full p-4 pl-12 pr-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300"
                placeholder="••••••••"
              />
              <FaLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {/* Botón de envío */}
          <div className="flex justify-end mt-8 pt-6 border-t border-gray-200">
            <button 
              type="submit" 
              className="px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white transition-all duration-300 hover:from-red-600 hover:to-red-700 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2 group ml-auto"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <FaUser />
                  {t('register.submit')}
                </>
              )}
            </button>
          </div>
        </form>

        {/* Enlace de navegación */}
        <div className="flex justify-center mt-8 pt-6 border-t border-gray-200">
          <p className="text-gray-600 text-sm">
            {t('auth.alreadyHaveAccount')}
            <a href="/auth/login" className="text-red-500 font-semibold ml-2 hover:underline transition-all duration-200 hover:text-red-600">
              {t('auth.login')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}; 